import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Cog, Wrench, Package, Shield } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import workshop1 from '../../../images/Workshop/Workshop1.jpeg';
import workshop2 from '../../../images/Workshop/Workshop2.jpeg';
import workshop3 from '../../../images/Workshop/Workshop3.jpeg';
import workshop4 from '../../../images/Workshop/Workshop4.jpeg';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './WorkshopService.css';

const WorkshopService = () => {
  const capabilities = [
    'Complete valve overhauling and refurbishment',
    'Precision machining of valve components',
    'Repair and reconditioning of valve seats, discs, plugs, and bushes',
    'Manufacturing of custom valve spare parts',
    'Hard facing and seat surface restoration',
    'Re-machining of damaged sealing surfaces',
    'Fabrication and repair of bypass assemblies',
    'Refurbishment of ARC valves and control valve internals'
  ];

  const machiningFacilities = [
    'CNC Machines',
    'VMC Machines',
    'Surface Grinding and lapping Machine',
    'Surface finishing and polishing equipment',
    'Stalliting / Hardfacing Welding set up'
  ];

  const sparePartsList = [
    'Vortex plugs and bushes',
    'Valve seats and seat rings',
    'Spindles and stems',
    'Discs and guides',
    'Orifice components',
    'Customized sealing components'
  ];

  const qualityAssurance = [
    'Dimensional accuracy',
    'Proper sealing performance',
    'Reliable valve operation',
    'Compliance with industrial standards'
  ];

  return (
    <div className="workshop-service-page">
      {/* Hero Section */}
      <section className="workshop-hero" style={{ backgroundImage: `url(${workshop1})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-hero-content"
          >
            <Breadcrumb />
            <h1 className="workshop-hero-title">
              Workshop <span className="workshop-hero-title-highlight">Support Services</span>
            </h1>
            <p className="workshop-hero-description">
              State-of-the-art workshop facility with advanced machining and repair infrastructure 
              for complete refurbishment, manufacturing, and servicing of industrial valves and critical components.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="workshop-overview">
        <div className="container-custom">
          <div className="workshop-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="workshop-overview-content"
            >
              <h2 className="workshop-overview-title">Our Workshop Facility</h2>
              <p className="workshop-overview-description">
                At KirBOO Flowsafe Pvt. Ltd., our state-of-the-art workshop facility is equipped with advanced 
                machining and repair infrastructure to support complete refurbishment, manufacturing, and 
                servicing of industrial valves and critical components. Our workshop plays a key role in 
                supporting plant shutdowns, emergency repairs, and precision component manufacturing.
              </p>
              <p className="workshop-overview-description">
                With skilled technicians and modern equipment, we ensure quick turnaround time and high-quality 
                restoration of valves to meet original operating standards.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="workshop-overview-visual"
            >
              <div className="workshop-image-gallery">
                {[
                  { src: workshop1, alt: 'Workshop support services' },
                  { src: workshop2, alt: 'Workshop facility' },
                  { src: workshop3, alt: 'Machining and repair' },
                  { src: workshop4, alt: 'Quality restoration' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="workshop-image-card"
                  >
                    <div className="workshop-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Workshop Capabilities */}
      <section className="workshop-section workshop-section-alt">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-section-header"
          >
            <h2 className="workshop-section-title">Workshop Capabilities</h2>
          </motion.div>
          <ul className="workshop-list">
            {capabilities.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                className="workshop-list-item"
              >
                <CheckCircle className="workshop-list-icon" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Machining Facilities */}
      <section className="workshop-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-section-header"
          >
            <h2 className="workshop-section-title">Machining Facilities</h2>
            <p className="workshop-section-description">
              Our workshop is equipped with modern machining equipment to support precision manufacturing and repair, including:
            </p>
          </motion.div>
          <ul className="workshop-list workshop-list-grid">
            {machiningFacilities.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="workshop-list-item"
              >
                <Cog className="workshop-list-icon" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Spare Parts Manufacturing */}
      <section className="workshop-section workshop-section-alt">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-section-header"
          >
            <h2 className="workshop-section-title">Spare Parts Manufacturing</h2>
            <p className="workshop-section-description">
              We manufacture and supply a wide range of valve spare parts to support shutdown and maintenance requirements, including:
            </p>
          </motion.div>
          <ul className="workshop-list workshop-list-grid">
            {sparePartsList.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="workshop-list-item"
              >
                <Package className="workshop-list-icon" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Shutdown & Emergency Support */}
      <section className="workshop-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-shutdown-block"
          >
            <Wrench className="workshop-shutdown-icon" />
            <h2 className="workshop-section-title">Shutdown & Emergency Support</h2>
            <p className="workshop-overview-description">
              Our workshop is capable of rapid spare manufacturing and component repair during plant shutdowns, 
              ensuring that critical valves are restored and returned to service within the scheduled shutdown period.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="workshop-section workshop-section-alt">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-section-header"
          >
            <h2 className="workshop-section-title">Quality Assurance</h2>
            <p className="workshop-section-description">
              All repair and machining activities are carried out under strict quality control procedures to ensure:
            </p>
          </motion.div>
          <ul className="workshop-list workshop-list-grid">
            {qualityAssurance.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="workshop-list-item"
              >
                <Shield className="workshop-list-icon" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA Section */}
      <section className="workshop-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="workshop-cta-content"
          >
            <h2 className="workshop-cta-title">Need Workshop Support?</h2>
            <p className="workshop-cta-description">
              Get a free consultation and quote for refurbishment, spare manufacturing, or shutdown support
            </p>
            <div className="workshop-cta-form">
              <EnquiryForm serviceName="Workshop Support" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WorkshopService;
